import * as React from 'react';

import type { TopReview } from 'bundles/reviews-common/types/reviewsSharedTypes';
import ReviewsBackButton from 'bundles/reviews/components/ReviewsBackButton';
import ReviewsPageLayout from 'bundles/reviews/components/ReviewsPageLayout';
import ReviewsProductCard from 'bundles/reviews/components/ReviewsProductCard';
import ReviewsRatingCard from 'bundles/reviews/components/ReviewsRatingCard';
import ReviewsTable from 'bundles/reviews/components/ReviewsTable';
import TopReviewsSection from 'bundles/reviews/components/TopReviewsSection';
import type {
  ReviewsHeaderData,
  ReviewsPageCourseInfo,
  ReviewsPageSchemaReview,
} from 'bundles/reviews/hooks/useReviewsPageData';
import CourseReviewsPageSeoElements from 'bundles/reviews/pages/course/CourseReviewsPageSeoElements';
import type { ReviewRouter } from 'bundles/reviews/types/reviewTypes';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';

import _t from 'i18n!nls/reviews';

const NUM_TOP_REVIEWS_TO_SHOW = 12;

type Props = {
  courseInfo: ReviewsPageCourseInfo;
  headerData: ReviewsHeaderData;
  topReviews: TopReview[];
  schemaReviews: ReviewsPageSchemaReview[];
  router: ReviewRouter;
  programSlug?: string;
  orgSlug?: string;
};

const CourseReviewsPageContent = ({
  courseInfo,
  headerData,
  topReviews,
  schemaReviews,
  router,
  programSlug,
  orgSlug,
}: Props) => {
  const isInUnifiedPageRollout = useIsOrgScopedHome();
  const courseId = courseInfo.id.split('~')[1];

  let coursePageHref = `/learn/${courseInfo.slug}`;
  if (isInUnifiedPageRollout && orgSlug) {
    coursePageHref = `/organizations/${orgSlug}/learn/${courseInfo.slug}`;
  } else if (programSlug) {
    coursePageHref = `/programs/${programSlug}/learn/${courseInfo.slug}`;
  }

  return (
    <ReviewsPageLayout
      backButton={<ReviewsBackButton courseName={courseInfo.name} href={coursePageHref} />}
      productCard={
        <ReviewsProductCard
          {...headerData}
          programSlug={programSlug}
          courseId={courseId ?? ''}
          courseName={headerData.courseName || courseInfo.name}
        />
      }
      ratingCard={
        <ReviewsRatingCard
          averageFiveStarRating={headerData.averageFiveStarRating}
          ratingCount={headerData.ratingCount}
          ratingCountsByStar={headerData.ratingCountsByStar}
        />
      }
      seoElements={
        <CourseReviewsPageSeoElements courseInfo={courseInfo} schemaReviews={schemaReviews} router={router} />
      }
    >
      <TopReviewsSection
        title={_t('Featured reviews')}
        reviews={topReviews
          .slice(0, NUM_TOP_REVIEWS_TO_SHOW)
          .filter((review) => review?.authorName && typeof review?.averageFiveStarRating === 'number')
          .map((review) => ({
            key: review.timestamp,
            authorName: review.authorName,
            comment: review.comment,
            rating: review.averageFiveStarRating,
            timestamp: review.timestamp,
          }))}
      />
      <ReviewsTable courseId={courseInfo.id} />
    </ReviewsPageLayout>
  );
};

export default CourseReviewsPageContent;
